<template>
  <div class="sections-container page-layout">
    <section class="hero" :style="heroSectionStyle">
      <div class="backdrop" />
      <div class="wrapper">
        <img class="logo" :alt="`Logo de ${agency.identity.companyName}`" :src="agency.logo" />
        <div class="identity">
          <h1 class="title">{{ agency?.identity.companyName }}</h1>
          <div class="sub-title">{{ address }}</div>
        </div>
      </div>
    </section>

    <div class="content sections-container">
      <section class="block-section">
        <h2 class="title">Notre agence immobilière à {{ agency.identity.city }}</h2>
        <div class="content">
          <announces-text-with-read-more :text="agency.description" />
        </div>
      </section>

      <div class="separator" />

      <section class="block-section">
        <annonces-with-pagination
          title="Annonces immobilières"
          :empty-state-text="`${agency?.identity.companyName} ne propose pas d’annonce pour le moment.`"
          :criteria="realEstateAdsSearchCriteria"
        />
      </section>

      <div class="separator u-hide-desktop" />
    </div>

    <section class="contact-form">
      <div class="form-contact">
        <div class="header">
          <div class="agency">
            <img
              width="48"
              height="48"
              class="logo"
              :alt="`Logo de ${agency.identity.companyName}`"
              :src="agency.logo"
            />
            <div class="metas">
              <div class="name">
                {{ agency.identity.companyName }}
              </div>
              <announces-form-phone-contact
                v-if="agency.reachable"
                :on-submit-and-reveal-handler="handlePhoneContactFormSubmit"
              />
            </div>
          </div>
        </div>

        <div class="separator u-hide-mobile" />
        <template v-if="agency.reachable">
          <uikit-button
            v-if="!hasBeenContacted"
            class="u-hide-mobile"
            type="primary"
            @click="handleSendMessage"
          >
            Contacter
          </uikit-button>
          <template v-else>
            <div class="success">Votre demande a bien été prise en compte.</div>
          </template>
        </template>
        <template v-else>
          <announces-re-contact-me-form
            v-if="!hasBeenContacted"
            class="u-hide-mobile"
            :on-success="handleSendMessageSuccess"
            :handle-submit-contact-form="handleReContactMeFormSubmit"
          />

          <template v-else>
            <div class="success">Votre demande a bien été prise en compte.</div>
          </template>
        </template>
      </div>
    </section>
    <div class="mobile-contact-cta u-hide-desktop">
      <uikit-button v-if="agency.reachable" class="u-w-full" type="primary" @click="handleSendMessage">
        Contacter
      </uikit-button>
      <uikit-button v-else class="u-w-full" type="primary" @click="handleSendRecontactMe">
        Être recontacté(e) par l’agence
      </uikit-button>
    </div>
  </div>
  <agency-list-section
    :city="agency.identity.city"
    :postal-code="agency.identity.postalCode"
    :exclude-ids="[agency.id]"
  />
</template>

<script setup>
import { useDrawerStore, UikitButton } from '@hz/ui-kit'
import DrawerFormContact from '@/components/Announces/DrawerContact.vue'
import DrawerReContactMe from '@/components/Announces/DrawerReContactMe.vue'

const config = useRuntimeConfig()
const route = useRoute()
const agenciesApi = useAgenciesApi()

const hasBeenContacted = ref(false)

const { data: agency } = await useAsyncData(() => agenciesApi.getAgencyById(route.params.id), {
  default: () => null,
})

if (!agency.value || !agency.value.published) {
  throw createError({ statusCode: 404, statusMessage: 'agency-not-found' })
}

const address = computed(() => {
  if (!agency.value) return ''
  const { address, city, postalCode } = agency.value.identity

  return `${address}, ${postalCode} ${city}`
})

const heroSectionStyle = computed(() => {
  if (!agency.value) return ''

  return {
    backgroundImage: `url(${agency.value.cover})`,
  }
})
const realEstateAdsSearchCriteria = computed(() => {
  return {
    agencies: [agency.value.id],
  }
})

const handleContactFormSubmit = async (contactFormData) => {
  await useAgenciesApi().sendAdContactForm(agency.value.id, contactFormData)
  await useTracking().trackGA4Event('lead_agence')
}

const handleReContactMeFormSubmit = async (reContactMeFormData) => {
  await useAgenciesApi().sendReContactMeForm(agency.value.id, reContactMeFormData)
  await useTracking().trackGA4Event('lead_agence')
}

const handlePhoneContactFormSubmit = async (reCaptchaToken) => {
  const res = await useAgenciesApi().getAgencyPhone(agency.value.id, {
    reCaptchaToken: reCaptchaToken,
  })

  await useTracking().trackGA4Event('lead_agence')

  return res.phone
}

const handleSendMessageSuccess = () => {
  hasBeenContacted.value = true
}

const handleSendMessage = async () => {
  useDrawerStore().addDrawer({
    id: 'drawer-ad-agency-contact',
    component: shallowRef(DrawerFormContact),
    props: {
      onSuccess: handleSendMessageSuccess,
      handleSubmitContactForm: handleContactFormSubmit,
      agencyName: agency.value.identity.companyName,
      agencyLogoUrl: agency.value.logo,
      handleDisplayPhoneNumber: handlePhoneContactFormSubmit,
    },
  })
}

const handleSendRecontactMe = async () => {
  useDrawerStore().addDrawer({
    id: 'drawer-ad-agency-re-contact-me',
    component: shallowRef(DrawerReContactMe),
    props: {
      onSuccess: handleSendMessageSuccess,
      handleSubmitContactForm: handleReContactMeFormSubmit,
      agencyName: agency.value.identity.companyName,
      agencyLogoUrl: agency.value.logo,
    },
  })
}

// Head metas
const pageUrl = `${config.public.hpBaseUrl}${route.path}`
const pageTitle = `${agency.value?.identity.companyName} | Agence immobilière à ${agency.value?.identity.city}`
const description = `Découvrez l'agence immobilière ${agency.value?.identity.companyName} et ses offres pour un investissement rentable.`
const featureImageUrl = agency.value?.cover
const featureImageAltText = `Photo (${pageTitle})`

useServerSeoMeta({
  title: pageTitle,
  description: description,
})

useHead({
  meta: [
    {
      hid: 'og:title',
      property: 'og:title',
      content: pageTitle,
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: featureImageUrl,
    },
    {
      hid: 'og:url',
      property: 'og:url',
      content: pageUrl,
    },
    {
      hid: 'og:site_name',
      property: 'og:site_name',
      content: 'Horiz.io',
    },
    {
      hid: 'og:type',
      property: 'og:type',
      content: 'article',
    },
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      hid: 'twitter:image:alt',
      name: 'twitter:image:alt',
      content: featureImageAltText,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: pageUrl,
    },
  ],
})

const breadcrumb = [
  {
    '@type': 'ListItem',
    position: 1,
    name: 'accueil',
    item: config.public.hpBaseUrl,
  },
  {
    '@type': 'ListItem',
    position: 2,
    name: pageTitle,
    item: pageUrl,
  },
]

useJsonld([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Horiz',
    alternateName: 'Rendement Locatif',
    url: config.public.hpBaseUrl,
    logo: `${config.public.hpBaseUrl}/imgs/logo.png`,
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    url: config.public.hpBaseUrl,
    name: 'Horiz',
    alternateName: 'Rendement locatif',
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb,
  },
  {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: pageTitle,
    speakable: {
      '@type': 'SpeakableSpecification',
      xpath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
    },
    url: pageUrl,
  },
])

onMounted(async () => {
  if (import.meta.client) {
    useHead({
      script: [
        {
          src: `https://www.google.com/recaptcha/enterprise.js?render=${config.public.adReCaptchaSiteKey}`,
          async: true,
          defer: true,
          tagPosition: 'head',
        },
      ],
    })
  }

  if (agency.value) {
    const trackingAttributes = {
      id: agency.value.id,
      name: agency.value.identity.companyName,
      city: agency.value.identity.city,
    }
    await useTracking().trackAfterMounted('[Page agence BtoB] Visite d’une page agence', trackingAttributes)
    await useTracking().trackGA4EventAfterMounted('page_agence_vue')
  }
})
</script>

<style lang="less" scoped>
.sections-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.block-section {
  h2.title {
    color: var(--ds-color-primary-100);
    margin-bottom: 12px;
    font-size: 26px;
    font-weight: var(--ds-weight-semi-bold);
  }
}

.separator {
  width: 100%;
  height: 1px;
  background-color: var(--ds-color-gray-25);
}

@media @bp-lg {
  .sections-container {
    gap: 32px;
  }

  .block-section {
    h2.title {
      margin-bottom: 24px;
    }
  }
}
</style>

<style lang="less" scoped>
.page-layout {
  width: 100%;
  padding: 0 15px;
  margin: auto;
  display: grid;
  grid-template-areas:
    'hero'
    'content'
    'contact-form';
}

.hero {
  position: relative;
  grid-area: hero;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  margin-left: -15px;
  margin-right: -15px;

  .backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backdrop-filter: blur(2px);
    background: rgba(0, 0, 0, 0.45);
  }

  .wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
    padding: 48px 15px;

    .logo {
      width: 60px;
      height: 60px;
      border-radius: 60px;
    }

    .identity {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    h1.title {
      color: var(--ds-color-white-100);
      font-size: 24px;
      font-style: normal;
      font-weight: var(--ds-weight-semi-bold);
    }
    .sub-title {
      color: var(--ds-color-gray-50);
      font-size: 16px;
      font-style: normal;
      font-weight: var(--ds-weight-regular);
    }
  }
}

.content {
  grid-area: content;
}

.contact-form {
  grid-area: contact-form;
}

.agencies {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 32px;
  padding: 48px 15px;
  background: var(--ds-color-primary-25);

  h2.title {
    color: var(--ds-color-gray-100);
    font-size: 28px;
    font-style: normal;
    font-weight: var(--ds-weight-semi-bold);

    span {
      color: var(--ds-color-primary-100);
    }
  }

  .info {
    color: var(--ds-color-gray-50);
    font-size: 16px;
    font-style: normal;
    font-weight: var(--ds-weight-regular);
  }
}

@media @bp-lg {
  .page-layout {
    padding: 0 50px;
    grid-template-columns: auto minmax(auto, 420px);
    grid-column-gap: 58px;
    grid-template-areas:
      'hero contact-form'
      'content contact-form';
  }

  .hero {
    grid-row: 1;
    grid-column: hero-start / contact-form-end;
    margin-left: -50px;
    margin-right: -50px;
    padding-left: 50px;
    padding-right: 50px;
    gap: 40px;

    .wrapper {
      flex-direction: row;
      justify-content: flex-start;
      padding-left: 0;
      width: calc(100% - 420px);

      .identity {
        align-items: flex-start;
      }

      .logo {
        width: 80px;
        height: 80px;
        border-radius: 80px;
      }

      h1.title {
        font-size: 42px;
      }
      .sub-title {
        font-size: 24px;
      }
    }
  }

  .agencies {
    padding-left: 50px;
    padding-right: 50px;
  }
}

//-------- contact form
.form-contact {
  width: 100%;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 8px;
  border: 1px solid var(--ds-color-gray-10);
  background: var(--ds-color-white-100);

  .header {
    .agency {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 18px;

      .logo {
        width: 48px;
        height: 48px;
        border-radius: 50%;
      }
      .metas {
        display: flex;
        flex-direction: column;
      }
      .name {
        font-size: 18px;
        font-weight: var(--ds-weight-semi-bold);
      }
    }
  }
}

.mobile-contact-cta {
  padding: 20px 32px;
  border-top: 1px solid var(--ds-color-gray-10);
  background: var(--ds-color-white-100);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

@media @bp-lg {
  .contact-form {
    margin-top: 32px;
    max-width: 420px;

    :deep(.form-contact) {
      position: sticky;
      top: 80px;
    }
  }
}
</style>
